import { Check, CircleCheck, CircleX, Scissors, X } from "lucide-react";

export interface ButtonProps {
  /** What background color to use */
  backgroundColor?: string;
  /** How large should the button be? */
  size?: 'small' | 'middle' | 'large';
  /** kind of situation */
  situation?: 'selected' | 'correct' | 'wrong' | 'default' | 'answer_key';
  /** Button contents */
  label: string;
  letter?: string;
  internalText?: string;
  disabled?: boolean;
  /** Eliminada pela tesoura: riscada, esmaecida e sem clique — só o botão da tesoura restaura. */
  eliminated?: boolean;
  /** Optional click handler */
  onClick?: () => void;
}

const cx = (...v: Array<string | false | null | undefined>) =>
  v.filter(Boolean).join(' ');

/**
 * Alternativa de questão (componente "Alternativa" do design).
 *
 * Anatomia: badge circular com a letra à esquerda, texto no meio e um selo de
 * estado à direita quando respondida ("Gabarito" / "Sua resposta"). A seleção
 * usa o tratamento único de selecionado (select/brand); verde e vermelho são
 * reservados ao resultado — nunca a ênfase.
 */
export const ChoiceButton = ({
  label,
  situation = 'default',
  letter = '',
  disabled = false,
  eliminated = false,
  internalText,
  onClick,
}: ButtonProps) => {
  const interactive =
    !disabled &&
    !eliminated &&
    (situation === 'default' || situation === 'selected') &&
    Boolean(onClick);

  const card = cx(
    'flex w-full items-center gap-3.5 rounded-chip border px-4 py-3 text-left',
    'transition-colors duration-150 motion-reduce:transition-none select-none',
    situation === 'default' &&
      cx(
        eliminated ? 'border-edge bg-surface-muted' : 'border-edge bg-surface',
        interactive && 'cursor-pointer hover:border-edge-strong hover:bg-surface-hover',
      ),
    situation === 'selected' &&
      cx('border-select/[0.42] bg-select/[0.14]', interactive && 'cursor-pointer'),
    situation === 'correct' && 'border-ok/40 bg-ok/[0.10]',
    situation === 'wrong' && 'border-danger/40 bg-danger/[0.08]',
    situation === 'answer_key' && 'border-warn/40 bg-warn/[0.10]',
    disabled && 'cursor-not-allowed opacity-60',
  );

  const badge = cx(
    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-[1.5px]',
    'text-[13px] font-semibold',
    situation === 'default' &&
      (eliminated ? 'border-edge text-ink-muted' : 'border-edge-strong text-ink-secondary'),
    situation === 'selected' && 'border-brand bg-brand text-brand-contrast',
    situation === 'correct' && 'border-ok bg-ok text-white dark:text-[#0f1420]',
    situation === 'wrong' && 'border-danger bg-danger text-white dark:text-[#0f1420]',
    situation === 'answer_key' && 'border-warn text-warn-ink',
  );

  const text = cx(
    'min-w-0 flex-1 text-[15px] leading-snug',
    situation === 'default' &&
      (eliminated ? 'text-ink-muted line-through' : 'text-ink-secondary'),
    situation === 'selected' && 'text-ink',
    (situation === 'correct' || situation === 'wrong') && 'text-ink',
    situation === 'answer_key' && 'text-warn-ink',
  );

  const status =
    situation === 'correct' ? (
      <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-ok-ink">
        <CircleCheck size={15} aria-hidden />
        Gabarito
      </span>
    ) : situation === 'wrong' ? (
      <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-danger-ink">
        <CircleX size={15} aria-hidden />
        Sua resposta
      </span>
    ) : eliminated ? (
      <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-ink-muted">
        <Scissors size={15} aria-hidden />
        Eliminada
      </span>
    ) : null;

  const handleClick = () => {
    if (disabled || eliminated) return;
    onClick?.();
  };

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
      onClick={handleClick}
      className={cx(
        card,
        interactive &&
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
      )}
    >
      {letter ? (
        <span className={badge}>
          {situation === 'correct' ? (
            <Check size={15} strokeWidth={3} aria-hidden />
          ) : situation === 'wrong' ? (
            <X size={15} strokeWidth={3} aria-hidden />
          ) : (
            letter
          )}
        </span>
      ) : null}

      <div className="min-w-0 flex-1">
        <span className={text}>{label}</span>

        {internalText ? (
          <div
            className={cx(
              'mt-3 border-t pt-3 text-sm leading-relaxed text-ink-secondary',
              situation === 'correct' && 'border-ok/30',
              situation === 'wrong' && 'border-danger/30',
              situation === 'answer_key' && 'border-warn/30',
              situation === 'selected' && 'border-select/30',
              situation === 'default' && 'border-edge',
            )}
          >
            {internalText}
          </div>
        ) : null}
      </div>

      {status}
    </div>
  );
};
